import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { useDisclosure } from '@chakra-ui/hooks';
import SuccessModal from '../myOffer/components/SuccessModal';
import { SiGooglesheets } from 'react-icons/si';
import * as XLSX from 'xlsx';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    MdCheckCircle,
    MdHourglassEmpty,
    MdWarning,
    MdCancel,
    MdVerified,
    MdLeaderboard,
    MdSearch,
    MdAutoAwesome,
    MdDoneAll,
    MdFileDownload,
    MdExpandMore,
    MdVerifiedUser,
    MdStorefront,
} from 'react-icons/md';

// ─── Status helpers ────────────────────────────────────────────────────────────

type LeadStatus = {
    label: string;
    color: 'gray' | 'yellow' | 'orange' | 'green' | 'emerald' | 'red';
    icon: React.ReactNode;
    description: string;
};

const getLeadStatus = (item: any): LeadStatus => {
    if (item.isPaid || item.paidAt) {
        return {
            label: 'Payé',
            color: 'emerald',
            icon: <MdCheckCircle size={14} />,
            description: 'Le virement a été effectué',
        };
    }
    switch (item.approvalStatus) {
        case 'approved':
            return {
                label: 'Comptabilisé',
                color: 'green',
                icon: <MdVerified size={14} />,
                description: 'Lead validé, sera facturé',
            };
        case 'conflict':
            return {
                label: 'En litige',
                color: 'orange',
                icon: <MdWarning size={14} />,
                description: "Lead contesté, en cours d'analyse",
            };
        case 'rejected':
            return {
                label: 'Non facturé',
                color: 'red',
                icon: <MdCancel size={14} />,
                description: 'Contestation acceptée, aucun paiement dû',
            };
        case 'pending':
        default:
            return {
                label: 'En vérification',
                color: 'yellow',
                icon: <MdHourglassEmpty size={14} />,
                description: 'En attente de validation (48h)',
            };
    }
};

const STATUS_COLOR_CLASSES: Record<string, { badge: string }> = {
    gray:    { badge: 'bg-leadrs-stroke text-leadrs-text-soft' },
    yellow:  { badge: 'bg-leadrs-yellow-soft text-leadrs-yellow-focus' },
    orange:  { badge: 'bg-leadrs-brown-soft text-leadrs-brown-focus' },
    green:   { badge: 'bg-leadrs-green-soft text-leadrs-green-focus' },
    emerald: { badge: 'bg-leadrs-green-soft text-leadrs-green-focus' },
    red:     { badge: 'bg-leadrs-red-soft text-leadrs-red-focus' },
};

type BodyEntry = { field: string; answer: string };
type Lead = {
    _id: string;
    offerBuyerNumber?: string | number;
    offerId?: string;
    // Locked offer that received the lead through the Batiweb waterfall (offerId is unset then).
    waterfallOfferId?: string;
    purchaseOrderId?: string;
    isPurchaseOrderLead?: boolean;
    createdAt?: string;
    channels?: string[];
    index?: number;
    totalLeads?: number;
    leadrsRate: number;
    customerRate: number;
    body?: BodyEntry[];
    comment?: string;
    reasons?: string[];
    status?: string;
    viewed?: boolean;
    approvalStatus?: 'pending' | 'approved' | 'rejected' | 'conflict';
    isPaid?: boolean;
    paidAt?: string;
    etiquetteDPE?: string | null;
    typeEnergieChauffage?: string | null;
    // Buyer-visible RGPD consent view (see sanitizeLeadForUser / buildBuyerConsentView):
    // proof + timestamp + browser + the sentence with the host site blanked to "ce site".
    // Older leads have no record at all.
    gdprConsent?: {
        accepted?: boolean;
        consentedAt?: string;
        text?: string;
        userAgent?: string;
        language?: string;
    };
};

type OfferSummary = {
    value: string;
    label: string;
    total: number;
    newCount: number;
    accepted: number;
    rejected: number;
    startedAt: string | null;
    latestAt: string | null;
    totalOrdered: number | null;
};

type ScopedSummary = {
    total: number;
    approved: number;
    pending: number;
    conflict: number;
    rejected: number;
    avgLeadrsRate: number;
    nouveau: number;
};

const LEADS_PAGE_SIZE = 50;

// Pseudo-offer, not a real campaign: selects leads bought through the Bourse du Lead
// marketplace (see leadMarketplace/index.tsx). Mirrors BOURSE_OFFER_KEY in the backend's
// leadController.ts — kept out of the `offers` list (server-side) since these leads never
// carry an offerId/purchaseOrderId to group by.
const BOURSE_OFFER_KEY = 'BOURSE_DU_LEAD';

type KpiCheckboxTone = 'yellow' | 'red' | 'green';

const KPI_TONE: Record<KpiCheckboxTone, { boxOn: string; boxOff: string }> = {
    yellow: { boxOn: 'bg-leadrs-yellow-focus border-leadrs-yellow-focus', boxOff: 'bg-white/70 border-leadrs-yellow-focus/30 hover:border-leadrs-yellow-focus/60' },
    red:    { boxOn: 'bg-leadrs-red-focus border-leadrs-red-focus',       boxOff: 'bg-white/70 border-leadrs-red-focus/30 hover:border-leadrs-red-focus/60' },
    green:  { boxOn: 'bg-leadrs-green-focus border-leadrs-green-focus',   boxOff: 'bg-white/70 border-leadrs-green-focus/30 hover:border-leadrs-green-focus/60' },
};

const DPE_COLORS: Record<string, string> = {
    A: 'bg-[#009A44] text-white',
    B: 'bg-[#51B848] text-white',
    C: 'bg-[#9ACA3C] text-white',
    D: 'bg-[#F5C300] text-gray-900',
    E: 'bg-[#F0891E] text-white',
    F: 'bg-[#E7401C] text-white',
    G: 'bg-[#C10A27] text-white',
};

const DpeBadge = ({ label }: { label: string | null | undefined }) => {
    if (!label) return <span className="text-xs text-gray-400">—</span>;
    const cls = DPE_COLORS[label.toUpperCase()] ?? 'bg-gray-200 text-gray-700';
    return (
        <span className={`inline-flex h-6 w-6 items-center justify-center rounded font-bold text-xs ${cls}`}>
            {label.toUpperCase()}
        </span>
    );
};

const parseBrowser = (ua?: string): string | null => {
    if (!ua) return null;
    if (/edg(a|ios|e)?\//i.test(ua)) return 'Edge';
    if (/opr\/|opera/i.test(ua)) return 'Opera';
    if (/firefox\/|fxios\//i.test(ua)) return 'Firefox';
    if (/chrome\/|crios\//i.test(ua)) return 'Chrome';
    if (/safari\//i.test(ua) && /version\//i.test(ua)) return 'Safari';
    return 'Autre navigateur';
};

const DEFAULT_CONSENT_TEXT =
    "J'accepte que mes coordonnées soient transmises par ce site à ses partenaires commerciaux pour recevoir leurs offres promotionnelles.";

type BuyerConsent = { accepted?: boolean; consentedAt?: string; text?: string; userAgent?: string; language?: string };

const ConsentBadge = ({ consent }: { consent?: BuyerConsent }) => {
    if (!consent || consent.accepted !== true) {
        return <span className="text-xs text-gray-400" title="Aucune preuve de consentement enregistrée">—</span>;
    }
    const when = consent.consentedAt ? new Date(consent.consentedAt) : null;
    const dateLabel = when ? when.toLocaleDateString('fr-FR') : null;
    const timeLabel = when ? when.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : null;
    const browser = parseBrowser(consent.userAgent);
    const sentence = consent.text || DEFAULT_CONSENT_TEXT;

    return (
        <div className="flex max-w-[260px] flex-col gap-1">
            <span className="inline-flex w-fit items-center gap-1 rounded-full bg-leadrs-green-soft px-2 py-1 font-geist text-xs font-semibold text-leadrs-green-focus">
                <MdVerifiedUser size={13} />
                Consenti
            </span>
            {when && (
                <span className="font-geist text-[11px] text-leadrs-text-soft">
                    {dateLabel} à {timeLabel}
                </span>
            )}
            {browser && (
                <span className="font-geist text-[11px] text-leadrs-text-soft">{browser}</span>
            )}
            <span className="font-geist text-[11px] italic leading-tight text-leadrs-text-soft" title={consent.userAgent}>
                « {sentence} »
            </span>
        </div>
    );
};

const KpiFilterCheckbox = ({
    checked,
    onChange,
    title,
    tone,
}: {
    checked: boolean;
    onChange: (checked: boolean) => void;
    title: string;
    tone: KpiCheckboxTone;
}) => {
    const { boxOn, boxOff } = KPI_TONE[tone];
    return (
        <label className="inline-flex cursor-pointer items-center" title={title}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="sr-only"
                aria-label={title}
            />
            <span
                className={`inline-flex h-[14px] w-[14px] items-center justify-center rounded border transition-all duration-150 ${checked ? boxOn : boxOff}`}
            >
                {checked && (
                    <svg viewBox="0 0 10 8" className="h-[7px] w-[7px] text-white" fill="none">
                        <path d="M1 4l2.5 2.5L9 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                )}
            </span>
        </label>
    );
};

const REASON_OPTIONS = [
    'Intéressé',
    'À rappeler',
    'Budget insuffisant',
    'Hors cible',
    'Ne répond pas',
    'Mauvaise identité',
    'Numéro invalide',
    'Pas intéressé',
];

const DEFAULT_TAGS = [
    'Non contacté',
    'NRP 1',
    'NRP 2',
    'NRP 3',
    'Pas intéressé',
    'RDV pris',
    'Converti',
];

const TAG_COLORS: Record<string, string> = {
    'Non contacté':  'bg-leadrs-stroke text-leadrs-text-soft border-leadrs-stroke',
    'NRP 1':         'bg-leadrs-brown-soft text-leadrs-brown-focus border-leadrs-brown-focus',
    'NRP 2':         'bg-leadrs-yellow-soft text-leadrs-yellow-focus border-leadrs-yellow-focus',
    'NRP 3':         'bg-leadrs-red-soft text-leadrs-red-focus border-leadrs-red-focus',
    'Pas intéressé': 'bg-leadrs-red-soft text-leadrs-red-focus border-leadrs-red-focus',
    'RDV pris':      'bg-leadrs-green-soft text-leadrs-green-focus border-leadrs-green-focus',
    'Converti':      'bg-leadrs-purple-soft text-leadrs-purple-focus border-leadrs-purple-focus',
};
const getTagColor = (tag: string) =>
    TAG_COLORS[tag] ?? 'bg-leadrs-purple-soft text-leadrs-purple-focus border-leadrs-purple-focus';

const MyLeadsBuyer = () => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    // Currently loaded page(s) of leads for the selected offer — NOT the buyer's full
    // lead set. Fetched 50 at a time from POST /lead/list, which does the offer/search/
    // status/comment/seen filtering server-side; only the tag filter (localStorage-only,
    // never sent to the backend) is applied client-side, in filteredLeads below.
    const [leads, setLeads] = useState<Lead[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    // Campaign dropdown entries + selected-offer KPIs, computed server-side via Mongo
    // aggregation (POST /lead/summary) instead of scanning every loaded lead client-side.
    const [offers, setOffers] = useState<OfferSummary[]>([]);
    const [scopedSummary, setScopedSummary] = useState<ScopedSummary>({ total: 0, approved: 0, pending: 0, conflict: 0, rejected: 0, avgLeadrsRate: 0, nouveau: 0 });
    const [marketplaceSummary, setMarketplaceSummary] = useState<{ total: number; newCount: number; latestAt: string | null }>({ total: 0, newCount: 0, latestAt: null });
    const [selectedOffer, setSelectedOffer] = useState<string>(() => localStorage.getItem('leads_selectedOffer') || '');
    const [search, setSearch] = useState<string>(() => localStorage.getItem('leads_search') || '');
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    // "Seen" state, one lastSeenAt timestamp per campaign/offer rather than one entry per
    // lead id — a per-id Set doesn't scale once leads are paginated (we'd only know the
    // ids of whatever page happened to be loaded). Only drives the "Nouveaux" counts and
    // per-row badge below (via isLeadNew) — it no longer filters which leads are fetched
    // (see buildFilterBody).
    const [lastSeenAt, setLastSeenAt] = useState<Record<string, string>>(() => {
        const stored = localStorage.getItem('leads_lastSeenAt');
        return stored ? JSON.parse(stored) : {};
    });
    const [offerDetails, setOfferDetails] = useState<Record<string, { type?: string; industry?: string; subIndustry?: string; regions?: string[] }>>({});
    const [exporting, setExporting] = useState(false);

    const [tags, setTags] = useState<string[]>(() => {
        const stored = localStorage.getItem('leadTags_list');
        return stored ? JSON.parse(stored) : DEFAULT_TAGS;
    });
    const [leadTags, setLeadTags] = useState<Record<string, string>>(() => {
        const stored = localStorage.getItem('leadTags_map');
        return stored ? JSON.parse(stored) : {};
    });
    const [selectedTagFilter, setSelectedTagFilter] = useState<string>(() => localStorage.getItem('leads_selectedTagFilter') || '');
    const [commentFilter, setCommentFilter] = useState<'all' | 'with' | 'without'>(() => (localStorage.getItem('leads_commentFilter') as 'all' | 'with' | 'without') || 'all');
    const [statusFilters, setStatusFilters] = useState<{
        pending: boolean;
        conflict: boolean;
        approved: boolean;
        rejected: boolean;
    }>(() => {
        const defaults = { pending: true, conflict: true, approved: true, rejected: true };
        const stored = localStorage.getItem('leads_statusFilters');
        // Merge over defaults so a value saved before the `rejected` filter existed still
        // defaults it to visible, instead of silently hiding rejected leads for existing users.
        return stored ? { ...defaults, ...JSON.parse(stored) } : defaults;
    });
    const [campaignSort, setCampaignSort] = useState<'new' | 'date'>(() => (localStorage.getItem('leads_campaignSort') as 'new' | 'date') || 'date');
    const [campaignSearch, setCampaignSearch] = useState<string>('');
    const [showTagManager, setShowTagManager] = useState(false);
    const [newTagInput, setNewTagInput] = useState('');
    const [isCampaignOpen, setIsCampaignOpen] = useState(false);
    const [openFilter, setOpenFilter] = useState<'tag' | 'comment' | null>(null);

    const commentTimers = useRef<Record<string, any>>({});
    const campaignDropdownRef = useRef<HTMLDivElement>(null);
    const filterBarRef = useRef<HTMLDivElement>(null);

    const initUser = () => {
        axios
            .get(`${CONFIG.apiUrl}/user/me`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
            })
            .then((res) => {
                const user = res.data;
                if (user.kyc_url) {
                    setTimeout(() => { window.location.href = user.kyc_url; }, 3000);
                }
                if (user && user.type !== 'buyer') {
                    window.location.href = '/my-request';
                }
            })
            .catch((err) => console.log('No user info found', err));
    };

    const selectedApprovalStatuses = () =>
        Object.entries(statusFilters)
            .filter(([, isSelected]) => isSelected)
            .map(([status]) => status);

    // Shared filter body for /lead/list and /lead/export (/lead/summary is unfiltered
    // aside from offerKey, so it doesn't use this). Backend treats a missing/empty
    // approvalStatus as "no restriction" (see
    // parseApprovalStatuses), which would show everything including rejected leads —
    // the opposite of the old client-side behavior where unchecking every status box
    // showed nothing. So an empty selection is handled locally (see fetchLeadsPage /
    // fetchExportLeads) instead of ever being sent to the backend.
    //
    // No seenOnly/"Nouveau" filter here on purpose: it required an offer to have been
    // explicitly marked read (via "Marquer lus") before anything could count as "seen",
    // which almost never happens — so unchecking "Nouveau" returned zero leads for
    // virtually every buyer, making every other filter look broken too. Removed rather
    // than fixed; "Nouveaux" is now a read-only count (see the KPI tile below).
    const buildFilterBody = (approvalStatuses: string[]) => {
        const body: any = { approvalStatus: approvalStatuses.join(',') };
        if (selectedOffer && selectedOffer !== 'ALL') body.offerKey = selectedOffer;
        if (debouncedSearch.trim()) body.search = debouncedSearch.trim();
        if (commentFilter !== 'all') body.commentFilter = commentFilter;
        return body;
    };

    const fetchLeadsPage = async (targetPage: number, { append = false }: { append?: boolean } = {}) => {
        const approvalStatuses = selectedApprovalStatuses();
        if (approvalStatuses.length === 0) {
            setLeads([]);
            setTotal(0);
            setTotalPages(1);
            setPage(1);
            setLoading(false);
            setLoadingMore(false);
            return;
        }
        const token = localStorage.getItem('token');
        if (!token) return;
        if (append) setLoadingMore(true); else setLoading(true);
        try {
            const res = await axios.post(
                `${CONFIG.apiUrl}/lead/list`,
                { ...buildFilterBody(approvalStatuses), page: targetPage, limit: LEADS_PAGE_SIZE },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const { leads: fetched, total: t, totalPages: tp, page: p } = res.data;
            setLeads((prev) => (append ? [...prev, ...(fetched || [])] : fetched || []));
            setTotal(t ?? 0);
            setTotalPages(tp ?? 1);
            setPage(p ?? targetPage);
        } catch (err) {
            console.log(err);
            if (!append) { setLeads([]); setTotal(0); setTotalPages(1); }
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const fetchSummary = async () => {
        const token = localStorage.getItem('token');
        if (!token) return;
        try {
            const body: any = { lastSeenAt };
            if (selectedOffer && selectedOffer !== 'ALL') body.offerKey = selectedOffer;
            const res = await axios.post(`${CONFIG.apiUrl}/lead/summary`, body, { headers: { Authorization: `Bearer ${token}` } });
            setOffers(res.data?.offers || []);
            setScopedSummary(res.data?.scoped || { total: 0, approved: 0, pending: 0, conflict: 0, avgLeadrsRate: 0, nouveau: 0 });
            setMarketplaceSummary(res.data?.marketplace || { total: 0, newCount: 0, latestAt: null });
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        if (!localStorage.getItem('token') || localStorage.getItem('token')!.length < 10) {
            window.location.href = '/auth/sign-up';
        }
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('created') === 'true' && !isOpen) onOpen();
        initUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounce the search box so every keystroke doesn't fire a request.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 350);
        return () => clearTimeout(t);
    }, [search]);

    // Campaign dropdown + KPIs: depends on which offer is selected (for the scoped KPIs)
    // and on lastSeenAt (for newCount / "Nouveaux").
    useEffect(() => {
        fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOffer, lastSeenAt]);

    // Table data: refetch page 1 whenever the offer or any server-side filter changes.
    useEffect(() => {
        fetchLeadsPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOffer, debouncedSearch, statusFilters, commentFilter]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (campaignDropdownRef.current && !campaignDropdownRef.current.contains(e.target as Node)) {
                setIsCampaignOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (filterBarRef.current && !filterBarRef.current.contains(e.target as Node)) {
                setOpenFilter(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        localStorage.setItem('leads_selectedOffer', selectedOffer);
        localStorage.setItem('leads_search', search);
        localStorage.setItem('leads_statusFilters', JSON.stringify(statusFilters));
        localStorage.setItem('leads_commentFilter', commentFilter);
        localStorage.setItem('leads_selectedTagFilter', selectedTagFilter);
        localStorage.setItem('leads_campaignSort', campaignSort);
    }, [selectedOffer, search, statusFilters, commentFilter, selectedTagFilter, campaignSort]);

    const timeAgo = (date: any) => {
        const diffInSeconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
        const months  = Math.floor(diffInSeconds / (3600 * 24 * 30));
        const days    = Math.floor(diffInSeconds / (3600 * 24));
        const hours   = Math.floor((diffInSeconds % (3600 * 24)) / 3600);
        const minutes = Math.floor((diffInSeconds % 3600) / 60);
        const seconds = diffInSeconds % 60;
        if (months  > 0) return `il y a ${months} mois`;
        if (days    > 0) return `il y a ${days} jour${days > 1 ? 's' : ''}`;
        if (hours   > 0) return `il y a ${hours}h`;
        if (minutes > 0) return `il y a ${minutes} min`;
        return `il y a ${seconds}s`;
    };

    const isWithin30Minutes = (date: any) => {
        const diffInMs = Math.abs(new Date(date).getTime() - Date.now());
        return diffInMs / (1000 * 60) <= 30;
    };

    const handleCustomerRateChange = (leadId: string, rate: number) => {
        const token = localStorage.getItem('token');
        axios
            .put(`${CONFIG.apiUrl}/lead/${leadId}/rate`, { rate }, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => {
                setLeads((prev) => prev.map((l) => l._id === leadId ? { ...l, customerRate: rate } : l));
            })
            .catch((err) => console.log(err));
    };

    const handleLeadChange = (leadId: string, status: string, comment: string) => {
        const token = localStorage.getItem('token');
        return axios
            .put(`${CONFIG.apiUrl}/lead/${leadId}/status`, { status, comment }, { headers: { Authorization: `Bearer ${token}` } })
            .then(() => {
                setLeads((prev) => prev.map((l) => l._id === leadId ? { ...l, status, comment } : l));
            });
    };

    const handleLeadProblem = async (leadId: string) => {
        const token = localStorage.getItem('token');
        try {
            await axios.put(
                `${CONFIG.apiUrl}/lead/${leadId}/approval-status`,
                { approvalStatus: 'conflict' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setLeads((prev) => prev.map((l) => l._id === leadId ? { ...l, approvalStatus: 'conflict' } : l));
            toast.success('Problème signalé', { autoClose: 1800 });
        } catch (err) {
            toast.error('Échec du signalement ❌');
        }
    };

    const scheduleCommentSave = (lead: Lead, newComment: string) => {
        setLeads((prev) => prev.map((l) => l._id === lead._id ? { ...l, comment: newComment } : l));
        const key = lead._id;
        if (commentTimers.current[key]) clearTimeout(commentTimers.current[key]);
        commentTimers.current[key] = setTimeout(async () => {
            try {
                await handleLeadChange(lead._id, lead.status ?? '', newComment);
                toast.success('Commentaire enregistré ✅', { autoClose: 1800 });
            } catch (e) {
                toast.error("Échec de l'enregistrement du commentaire ❌");
            }
        }, 800);
    };

    const handleSetLeadTag = (leadId: string, tag: string) => {
        setLeadTags((prev) => {
            const next = tag ? { ...prev, [leadId]: tag } : (() => { const n = { ...prev }; delete n[leadId]; return n; })();
            localStorage.setItem('leadTags_map', JSON.stringify(next));
            return next;
        });
    };

    const handleAddTag = () => {
        const t = newTagInput.trim();
        if (!t || tags.includes(t)) return;
        const next = [...tags, t];
        setTags(next);
        localStorage.setItem('leadTags_list', JSON.stringify(next));
        setNewTagInput('');
    };

    const handleDeleteTag = (tag: string) => {
        if (DEFAULT_TAGS.includes(tag)) return;
        const next = tags.filter((t) => t !== tag);
        setTags(next);
        localStorage.setItem('leadTags_list', JSON.stringify(next));
        setLeadTags((prev) => {
            const next2 = { ...prev };
            Object.keys(next2).forEach((id) => { if (next2[id] === tag) delete next2[id]; });
            localStorage.setItem('leadTags_map', JSON.stringify(next2));
            return next2;
        });
        if (selectedTagFilter === tag) setSelectedTagFilter('');
    };

    // Mirrors the backend's GROUP_KEY_EXPR (leadController.ts) exactly — used here only to
    // work out whether an individual loaded lead counts as "new" (see isLeadNew below).
    // Grouping/labeling/counting for the dropdown itself now happens server-side (offers
    // state, from /lead/summary), not by scanning the loaded leads.
    const offerKey = (l: Lead) =>
        (l.purchaseOrderId && `po_${l.purchaseOrderId}`) ||
        (l.offerId && String(l.offerId)) ||
        (l.waterfallOfferId && String(l.waterfallOfferId)) ||
        (l.offerBuyerNumber && String(l.offerBuyerNumber)) ||
        'Unknown';

    const isLeadNew = (l: Lead) => {
        const seenAt = lastSeenAt[offerKey(l)];
        if (!seenAt) return true;
        return !!l.createdAt && new Date(l.createdAt).getTime() > new Date(seenAt).getTime();
    };

    useEffect(() => {
        if (selectedOffer === BOURSE_OFFER_KEY) return;
        if (offers.length > 0 && (!selectedOffer || !offers.some((o) => o.value === selectedOffer))) {
            setSelectedOffer(offers[0].value);
        }
    }, [offers, selectedOffer]);

    const offerIds = useMemo(() => offers.map((o) => o.value).sort().join(','), [offers]);
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token || !offerIds) return;
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const ids = offerIds.split(',').filter((id) => id !== 'ALL' && id !== 'Unknown');
        if (ids.length === 0) return;
        const isObjectId = (s: string) => /^[a-f\d]{24}$/i.test(s);
        Promise.all(
            ids.map((id) => {
                if (id.startsWith('po_')) {
                    const poId = id.replace('po_', '');
                    if (!isObjectId(poId)) return null;
                    return axios.get(`${CONFIG.apiUrl}/purchase-order/${poId}`, config)
                        .then((r) => ({ id, data: r.data, isPurchaseOrder: true }))
                        .catch((): null => null);
                }
                if (!isObjectId(id)) return null;
                return axios.get(`${CONFIG.apiUrl}/offer/${id}`, config)
                    .then((r) => ({ id, data: r.data, isPurchaseOrder: false }))
                    .catch((): null => null);
            })
        ).then((results) => {
            const details: Record<string, { type?: string; industry?: string; subIndustry?: string; regions?: string[] }> = {};
            results.forEach((r) => {
                if (r?.data) {
                    if (r.isPurchaseOrder) {
                        details[r.id] = {
                            industry: r.data.industry,
                            subIndustry: r.data.product,
                            regions: r.data.departments[0] ? [r.data.departments[0]] : [],
                        };
                    } else {
                        details[r.id] = {
                            type: r.data.type,
                            industry: r.data.industry,
                            subIndustry: Array.isArray(r.data.subIndustry) ? r.data.subIndustry[0] || undefined : r.data.subIndustry || undefined,
                            regions: r.data.regions,
                        };
                    }
                }
            });
            setOfferDetails(details);
        });
    }, [offerIds]);

    // Offer, approval status, comment, and search filtering already happened server-side
    // (see fetchLeadsPage/buildFilterBody). Only the tag filter is applied here, since tags
    // are localStorage-only and were never sent to the backend.
    const filteredLeads = useMemo(() => {
        if (!selectedTagFilter) return leads;
        return leads.filter((l) => leadTags[l._id] === selectedTagFilter);
    }, [leads, selectedTagFilter, leadTags]);

    const bodyFields: string[] = useMemo(() => {
        const s = new Set<string>();
        filteredLeads.forEach((l) => l.body?.forEach((e) => e.field && s.add(e.field)));
        return Array.from(s);
    }, [filteredLeads]);

    const getBodyAnswer = (lead: Lead, field: string) =>
        lead.body?.find((e) => e.field === field)?.answer ?? '—';

    // KPI tiles for the selected offer, computed server-side (/lead/summary) instead of
    // by scanning every loaded lead — those only ever reflect the current page(s) anyway.
    const scopedApproved = scopedSummary.approved;
    const scopedPending = scopedSummary.pending;
    const scopedConflict = scopedSummary.conflict;
    const scopedRejected = scopedSummary.rejected;
    const scopedAvgLeadrsRate = scopedSummary.avgLeadrsRate;
    const scopedNouveau = scopedSummary.nouveau;

    const selectedOfferData = useMemo(() => {
        if (!selectedOffer || selectedOffer === 'ALL') return null;
        return offers.find((o) => o.value === selectedOffer) ?? null;
    }, [offers, selectedOffer]);

    const filteredOffersInDropdown = useMemo(() => {
        let list = [...offers];
        if (campaignSearch.trim()) {
            const q = campaignSearch.toLowerCase();
            list = list.filter(
                (o) =>
                    o.label.toLowerCase().includes(q) ||
                    (offerDetails[o.value]?.industry || '').toLowerCase().includes(q) ||
                    (offerDetails[o.value]?.subIndustry || '').toLowerCase().includes(q)
            );
        }
        if (campaignSort === 'new') {
            list.sort((a, b) => {
                if (b.newCount !== a.newCount) return b.newCount - a.newCount;
                if (!a.latestAt) return 1;
                if (!b.latestAt) return -1;
                return b.latestAt.localeCompare(a.latestAt);
            });
        } else {
            list.sort((a, b) => {
                if (!a.latestAt) return 1;
                if (!b.latestAt) return -1;
                return b.latestAt.localeCompare(a.latestAt);
            });
        }
        return list;
    }, [offers, campaignSearch, campaignSort, offerDetails]);

    const markOfferAsRead = (offerValue: string) => {
        setLastSeenAt((prev) => {
            const next = { ...prev, [offerValue]: new Date().toISOString() };
            localStorage.setItem('leads_lastSeenAt', JSON.stringify(next));
            return next;
        });
    };

    // Exports hit the dedicated, unpaginated /lead/export endpoint so CSV/XLSX always
    // reflect every lead matching the current filters, not just whatever page(s) happen
    // to be loaded in the table. The tag filter (client-only) is re-applied afterwards.
    const fetchExportLeads = async (): Promise<Lead[]> => {
        const approvalStatuses = selectedApprovalStatuses();
        if (approvalStatuses.length === 0) return [];
        const token = localStorage.getItem('token');
        if (!token) return [];
        const res = await axios.post(
            `${CONFIG.apiUrl}/lead/export`,
            buildFilterBody(approvalStatuses),
            { headers: { Authorization: `Bearer ${token}` } }
        );
        const data: Lead[] = res.data || [];
        return selectedTagFilter ? data.filter((l) => leadTags[l._id] === selectedTagFilter) : data;
    };

    const buildExportRows = (data: Lead[]) => {
        const exportBodyFields = Array.from(
            data.reduce((s, l) => { l.body?.forEach((e) => e.field && s.add(e.field)); return s; }, new Set<string>())
        );
        const staticHeaders = ['ID Mission', 'Ma note', 'Motif de signalement', 'Commentaire', 'Avancement', 'Note leadrs'];
        const headers = [staticHeaders[0], staticHeaders[1], staticHeaders[2], staticHeaders[3], ...exportBodyFields, ...staticHeaders.slice(4)];
        const rows = data.map((item) => {
            const bodyValues = exportBodyFields.map((f) => getBodyAnswer(item, f));
            const progress = item.totalLeads && item.totalLeads > 1 ? `${item.index}/${item.totalLeads}` : 'N/A';
            return [`#${item.offerBuyerNumber ?? ''}`, `${item.customerRate}/5`, (item.reasons ?? []).join(' / '), item.comment ?? '', ...bodyValues, progress, `${item.leadrsRate}/10`];
        });
        return { headers, rows };
    };

    const downloadCSV = async () => {
        setExporting(true);
        try {
            const data = await fetchExportLeads();
            if (!data.length) { toast.error('Aucun lead à exporter'); return; }
            const { headers, rows } = buildExportRows(data);
            const csvContent = [headers, ...rows].map((row) => row.map((field) => typeof field === 'string' ? `"${field.replace(/"/g, '""')}"` : field).join(',')).join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'mes_leads.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("Échec de l'export ❌");
        } finally {
            setExporting(false);
        }
    };

    const downloadXLSX = async () => {
        setExporting(true);
        try {
            const data = await fetchExportLeads();
            if (!data.length) { toast.error('Aucun lead à exporter'); return; }
            const { headers, rows } = buildExportRows(data);
            const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Leads');
            XLSX.writeFile(wb, 'mes_leads.xlsx');
        } catch (err) {
            toast.error("Échec de l'export ❌");
        } finally {
            setExporting(false);
        }
    };

    const LONG_FIELD_HINTS = ['description', 'message', 'besoin', 'comment', 'notes', 'remarque', 'détails', 'details', 'adresse'];
    const isLongField = (name: string) => LONG_FIELD_HINTS.some((h) => name.toLowerCase().includes(h) || name.length > 30);
    const colClassFor = (name: string) => isLongField(name) ? 'min-w-[260px] max-w-[540px]' : 'min-w-[120px] max-w-[260px]';

    return (
        <div className="h-full w-full">
            <ToastContainer position="bottom-right" />
            <SuccessModal isOpen={isOpen} onClose={onClose} />

            <div className="space-y-5">
                {/* Header */}
                <div className="rounded-2xl border border-leadrs-stroke bg-white">
                    <div className="relative bg-gradient-to-r from-leadrs-bg via-white to-leadrs-purple-soft/40 px-6 py-5">
                        <div className="flex items-start gap-4 xl:pr-[320px]">
                                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-leadrs-purple-soft">
                                    <MdLeaderboard className="h-5 w-5 text-leadrs-purple-focus" />
                                </div>
                                <div>
                                    <h2 className="font-crimson text-3xl font-semibold leading-tight text-leadrs-text">
                                        Pipeline des leads
                                    </h2>
                                    <p className="mt-1 max-w-3xl font-geist text-sm text-leadrs-text-soft">
                                        Même niveau d'information, lecture plus rapide: filtrez, priorisez et passez à l'action.
                                    </p>
                                </div>
                        </div>
                        <div ref={campaignDropdownRef} className="relative mt-4 xl:mt-0 xl:absolute xl:right-6 xl:top-5 xl:w-[300px]">
                            <p className="mb-1.5 font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-text-soft">
                                Campagnes
                            </p>
                            {/* Trigger */}
                            <button
                                onClick={() => { setIsCampaignOpen((v) => !v); setCampaignSearch(''); }}
                                className={`w-full rounded-xl border bg-white px-3 py-2.5 text-left transition-colors focus:outline-none ${isCampaignOpen ? 'border-leadrs-brand' : 'border-leadrs-stroke hover:border-leadrs-brand'}`}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                        {selectedOffer === BOURSE_OFFER_KEY ? (
                                            <>
                                                <p className="truncate font-geist text-sm font-semibold text-leadrs-purple-focus">
                                                    Bourse du Lead
                                                </p>
                                                <p className="mt-0.5 truncate font-geist text-[11px] text-leadrs-text-soft">
                                                    Leads achetés sur la Bourse{marketplaceSummary.latestAt ? ` · ${timeAgo(marketplaceSummary.latestAt)}` : ''}
                                                </p>
                                            </>
                                        ) : selectedOffer && selectedOffer !== 'ALL' ? (
                                            <>
                                                <p className="truncate font-geist text-sm font-semibold text-leadrs-text">
                                                    {selectedOfferData?.label ?? selectedOffer.toUpperCase()}
                                                </p>
                                                <p className="mt-0.5 truncate font-geist text-[11px] text-leadrs-text-soft">
                                                    {offerDetails[selectedOffer]?.subIndustry ?? offerDetails[selectedOffer]?.industry ?? 'Campagne'}{selectedOfferData?.latestAt ? ` · ${timeAgo(selectedOfferData.latestAt)}` : ''}
                                                </p>
                                            </>
                                        ) : (
                                            <>
                                                <p className="font-geist text-sm font-semibold text-leadrs-text">Toutes les campagnes</p>
                                                <p className="mt-0.5 font-geist text-[11px] text-leadrs-text-soft">
                                                    {offers.length} campagne{offers.length > 1 ? 's' : ''} · {total} leads
                                                </p>
                                            </>
                                        )}
                                    </div>
                                    <div className="flex flex-shrink-0 items-center gap-1.5">
                                        {selectedOffer === BOURSE_OFFER_KEY && marketplaceSummary.newCount > 0 && (
                                            <span className="rounded-full bg-leadrs-yellow-soft px-1.5 py-0.5 font-geist text-[10px] font-semibold text-leadrs-yellow-focus">
                                                +{marketplaceSummary.newCount}
                                            </span>
                                        )}
                                        {selectedOfferData && selectedOfferData.newCount > 0 && (
                                            <span className="rounded-full bg-leadrs-yellow-soft px-1.5 py-0.5 font-geist text-[10px] font-semibold text-leadrs-yellow-focus">
                                                +{selectedOfferData.newCount}
                                            </span>
                                        )}
                                        <MdExpandMore className={`h-4 w-4 flex-shrink-0 text-leadrs-text-soft transition-transform duration-200 ${isCampaignOpen ? 'rotate-180' : ''}`} />
                                    </div>
                                </div>
                            </button>
                            {/* Dropdown */}
                            {isCampaignOpen && (
                                <div className="absolute left-0 right-0 z-50 mt-1 w-full rounded-xl border border-leadrs-stroke bg-white shadow-md">
                                    {/* Search + filters within campaigns */}
                                    <div className="border-b border-leadrs-stroke p-2 space-y-2">
                                        {/* Search */}
                                        <div className="relative">
                                            <MdSearch className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-leadrs-text-soft" />
                                            <input
                                                type="text"
                                                placeholder="Chercher une campagne…"
                                                value={campaignSearch}
                                                onChange={(e) => setCampaignSearch(e.target.value)}
                                                className="h-8 w-full rounded-lg border border-leadrs-stroke bg-leadrs-bg pl-8 pr-3 font-geist text-xs focus:border-leadrs-brand focus:outline-none"
                                                autoFocus
                                            />
                                        </div>
                                        {/* Trier par */}
                                        <div className="flex items-center gap-1.5">
                                            <span className="flex-shrink-0 font-geist text-[11px] text-leadrs-text-soft">Trier par</span>
                                            <div className="flex flex-1 overflow-hidden rounded-lg border border-leadrs-stroke">
                                                {(['date', 'new'] as const).map((opt) => (
                                                    <button
                                                        key={opt}
                                                        onClick={() => setCampaignSort(opt)}
                                                        className={`flex-1 py-1 font-geist text-[11px] transition-colors focus:outline-none ${
                                                            campaignSort === opt
                                                                ? 'bg-leadrs-brand font-semibold text-white'
                                                                : 'bg-white text-leadrs-text-soft hover:bg-leadrs-bg'
                                                        }`}
                                                    >
                                                        {opt === 'new' ? 'Nouveaux' : 'Date'}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    {/* Bourse du Lead: a selectable pseudo-campaign (like a real offer row),
                                        not a redirect — selecting it filters the table to leads bought
                                        through the marketplace. Kept separate from the campaign list below
                                        since it isn't a real Offer/PurchaseOrder (see BOURSE_OFFER_KEY). */}
                                    <button
                                        onClick={() => { setSelectedOffer(BOURSE_OFFER_KEY); setIsCampaignOpen(false); }}
                                        className={`flex w-full items-center gap-2.5 border-b border-leadrs-stroke px-3 py-2.5 text-left transition-colors hover:bg-leadrs-purple-soft ${selectedOffer === BOURSE_OFFER_KEY ? 'bg-leadrs-purple-soft' : 'bg-leadrs-purple-soft/40'}`}
                                    >
                                        <MdStorefront className="h-4 w-4 flex-shrink-0 text-leadrs-purple-focus" />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate font-geist text-xs font-semibold text-leadrs-purple-focus">Bourse du Lead</p>
                                            <p className="truncate font-geist text-[11px] text-leadrs-text-soft">Leads achetés sur le marché</p>
                                        </div>
                                        {marketplaceSummary.total > 0 && (
                                            <span className="flex-shrink-0 font-geist text-[10px] text-leadrs-text-soft">
                                                {marketplaceSummary.total} lead{marketplaceSummary.total > 1 ? 's' : ''}
                                            </span>
                                        )}
                                        {marketplaceSummary.newCount > 0 && (
                                            <span className="flex-shrink-0 rounded-full bg-leadrs-yellow-soft px-1.5 py-0.5 font-geist text-[10px] font-semibold text-leadrs-yellow-focus">
                                                {marketplaceSummary.newCount} nouveaux
                                            </span>
                                        )}
                                    </button>
                                    <div className="max-h-[320px] overflow-y-auto">
                                    {filteredOffersInDropdown.map((o) => {
                                        const isSelected = selectedOffer === o.value;
                                        const detail = offerDetails[o.value];
                                        const isPO = o.value.startsWith('po_');
                                        return (
                                            <div key={o.value} className={`px-3 py-2.5 transition-colors hover:bg-leadrs-bg ${isSelected ? 'bg-leadrs-bg' : ''}`}>
                                                <button
                                                    onClick={() => { setSelectedOffer(o.value); setIsCampaignOpen(false); }}
                                                    className="w-full text-left"
                                                >
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="flex-shrink-0 rounded bg-leadrs-stroke px-1 py-0.5 font-geist text-[9px] font-semibold uppercase tracking-wider text-leadrs-text-soft">
                                                                    {isPO ? 'BC' : 'Offre'}
                                                                </span>
                                                                <p className="truncate font-geist text-xs font-semibold text-leadrs-text">{o.label}</p>
                                                            </div>
                                                            {detail?.industry && (
                                                                <p className="mt-0.5 truncate font-geist text-[11px] text-leadrs-text-soft">
                                                                    {detail.industry}{detail.subIndustry ? ` · ${detail.subIndustry}` : ''}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-shrink-0 flex-col items-end gap-1">
                                                            {o.newCount > 0 && (
                                                                <span className="rounded-full bg-leadrs-yellow-soft px-1.5 py-0.5 font-geist text-[10px] font-semibold text-leadrs-yellow-focus">
                                                                    {o.newCount} nouveaux
                                                                </span>
                                                            )}
                                                            <span className="font-geist text-[10px] text-leadrs-text-soft">{o.total} lead{o.total > 1 ? 's' : ''}</span>
                                                        </div>
                                                    </div>
                                                    <div className="mt-1 flex items-center gap-3">
                                                        {o.latestAt && (
                                                            <span className="font-geist text-[10px] text-leadrs-text-soft">Dernier: {timeAgo(o.latestAt)}</span>
                                                        )}
                                                        {o.accepted > 0 && (
                                                            <span className="font-geist text-[10px] text-leadrs-green-focus">{o.accepted} comptabilisé{o.accepted > 1 ? 's' : ''}</span>
                                                        )}
                                                    </div>
                                                </button>
                                                {isSelected && o.newCount > 0 && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); markOfferAsRead(o.value); setIsCampaignOpen(false); }}
                                                        className="mt-2 w-full rounded-lg bg-leadrs-yellow-soft px-2 py-1 font-geist text-[10px] font-semibold text-leadrs-yellow-focus transition-opacity hover:opacity-80"
                                                    >
                                                        Marquer lus ({o.newCount})
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 bg-white p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
                        {/* Read-only count, no filter checkbox: "seen" only updates via the
                            "Marquer lus" button per campaign, which almost nobody clicks, so a
                            filter toggle here returned zero leads for virtually every buyer. */}
                        <div className="rounded-xl border border-leadrs-stroke bg-leadrs-yellow-soft p-3 transition-shadow hover:shadow-sm">
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-geist text-xs font-medium text-leadrs-yellow-focus">Nouveaux</span>
                                <MdAutoAwesome className="h-4 w-4 text-leadrs-yellow-focus" />
                            </div>
                            <p className="mt-1 font-geist text-2xl font-semibold text-leadrs-yellow-focus">{scopedNouveau}</p>
                        </div>
                        <div className="rounded-xl border border-leadrs-stroke bg-leadrs-yellow-soft p-3 transition-shadow hover:shadow-sm">
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-geist text-xs font-medium text-leadrs-yellow-focus">Vérification en cours</span>
                                <div className="flex items-center gap-2">
                                    <MdHourglassEmpty className="h-4 w-4 text-leadrs-yellow-focus" />
                                    <KpiFilterCheckbox
                                        checked={statusFilters.pending}
                                        onChange={(checked) => setStatusFilters((prev) => ({ ...prev, pending: checked }))}
                                        tone="yellow"
                                        title="Filtrer Vérification en cours"
                                    />
                                </div>
                            </div>
                            <p className="mt-1 font-geist text-2xl font-semibold text-leadrs-yellow-focus">{scopedPending}</p>
                        </div>
                        <div className="rounded-xl border border-leadrs-stroke bg-leadrs-red-soft p-3 transition-shadow hover:shadow-sm">
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-geist text-xs font-medium text-leadrs-red-focus">En litige</span>
                                <div className="flex items-center gap-2">
                                    <MdWarning className="h-4 w-4 text-leadrs-red-focus" />
                                    <KpiFilterCheckbox
                                        checked={statusFilters.conflict}
                                        onChange={(checked) => setStatusFilters((prev) => ({ ...prev, conflict: checked }))}
                                        tone="red"
                                        title="Filtrer En litige"
                                    />
                                </div>
                            </div>
                            <p className="mt-1 font-geist text-2xl font-semibold text-leadrs-red-focus">{scopedConflict}</p>
                        </div>
                        <div className="rounded-xl border border-leadrs-stroke bg-leadrs-green-soft p-3 transition-shadow hover:shadow-sm">
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-geist text-xs font-medium text-leadrs-green-focus">Comptabilisé</span>
                                <div className="flex items-center gap-2">
                                    <MdDoneAll className="h-4 w-4 text-leadrs-green-focus" />
                                    <KpiFilterCheckbox
                                        checked={statusFilters.approved}
                                        onChange={(checked) => setStatusFilters((prev) => ({ ...prev, approved: checked }))}
                                        tone="green"
                                        title="Filtrer Comptabilisé"
                                    />
                                </div>
                            </div>
                            <p className="mt-1 font-geist text-2xl font-semibold text-leadrs-green-focus">{scopedApproved}</p>
                        </div>
                        {/* Green, not red: a rejected lead means we caught a bad-quality lead on your
                            behalf and didn't bill you for it — shown by default so that count matches
                            the campaign's total instead of silently disappearing from the table. */}
                        <div className="rounded-xl border border-leadrs-stroke bg-leadrs-green-soft p-3 transition-shadow hover:shadow-sm">
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-geist text-xs font-medium text-leadrs-green-focus">Rejetés (non facturés)</span>
                                <div className="flex items-center gap-2">
                                    <MdCancel className="h-4 w-4 text-leadrs-green-focus" />
                                    <KpiFilterCheckbox
                                        checked={statusFilters.rejected}
                                        onChange={(checked) => setStatusFilters((prev) => ({ ...prev, rejected: checked }))}
                                        tone="green"
                                        title="Filtrer Rejetés"
                                    />
                                </div>
                            </div>
                            <p className="mt-1 font-geist text-2xl font-semibold text-leadrs-green-focus">{scopedRejected}</p>
                        </div>
                        <div className="rounded-xl border border-leadrs-stroke bg-leadrs-purple-soft p-3 transition-shadow hover:shadow-sm">
                            <div className="flex items-center justify-between gap-2">
                                <span className="font-geist text-xs font-medium text-leadrs-purple-focus">Qualité moyenne</span>
                                <MdVerified className="h-4 w-4 text-leadrs-purple-focus" />
                            </div>
                            <p className="mt-1 font-geist text-2xl font-semibold text-leadrs-purple-focus">{scopedAvgLeadrsRate.toFixed(1)}/10</p>
                        </div>
                    </div>
                </div>

                {/* Search + filters + export */}
                <div className="rounded-xl border border-leadrs-stroke bg-white p-4">
                    <div className="flex flex-wrap items-center gap-2">

                        {/* Search */}
                        <div className="relative min-w-0 basis-full sm:basis-auto sm:flex-1">
                            <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-leadrs-text-soft" />
                            <input
                                type="search"
                                placeholder="Rechercher…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="h-10 w-full rounded-xl border border-leadrs-stroke bg-white pl-9 pr-3 font-geist text-sm text-leadrs-text focus:border-leadrs-brand focus:outline-none"
                            />
                        </div>

                        {/* Filter dropdowns */}
                        <div ref={filterBarRef} className="flex flex-wrap items-center gap-2">

                            {/* Tags dropdown */}
                            <div className="relative">
                                <button
                                    onClick={() => setOpenFilter(openFilter === 'tag' ? null : 'tag')}
                                    className={`inline-flex h-10 items-center gap-1.5 rounded-xl border px-3 font-geist text-sm transition-colors focus:outline-none ${
                                        selectedTagFilter
                                            ? 'border-leadrs-brand bg-leadrs-brand text-white'
                                            : openFilter === 'tag'
                                            ? 'border-leadrs-brand text-leadrs-brand'
                                            : 'border-leadrs-stroke text-leadrs-text-soft hover:border-leadrs-brand hover:text-leadrs-brand'
                                    }`}
                                >
                                    <span>{selectedTagFilter || 'Tags'}</span>
                                    <MdExpandMore className={`h-4 w-4 flex-shrink-0 transition-transform duration-150 ${openFilter === 'tag' ? 'rotate-180' : ''}`} />
                                </button>
                                {openFilter === 'tag' && (
                                    <div className="absolute left-0 top-[calc(100%+6px)] z-50 w-72 rounded-xl border border-leadrs-stroke bg-white p-3 shadow-md">
                                        <div className="mb-2.5 flex items-center justify-between">
                                            <span className="font-geist text-[11px] font-semibold uppercase tracking-widest text-leadrs-text-soft">Tag actif</span>
                                            {selectedTagFilter && (
                                                <button
                                                    onClick={() => setSelectedTagFilter('')}
                                                    className="font-geist text-[11px] text-leadrs-brand hover:underline"
                                                >
                                                    Effacer
                                                </button>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {tags.map((tag) => {
                                                // leads is already server-scoped to the selected offer, so no offerKey filter needed here.
                                                const count = leads.filter((l) => leadTags[l._id] === tag).length;
                                                return (
                                                    <button
                                                        key={tag}
                                                        onClick={() => { setSelectedTagFilter(selectedTagFilter === tag ? '' : tag); setOpenFilter(null); }}
                                                        className={`group relative rounded-full border px-2.5 py-1 font-geist text-[11px] font-medium transition-all ${
                                                            selectedTagFilter === tag
                                                                ? 'border-leadrs-brand bg-leadrs-brand text-white'
                                                                : `${getTagColor(tag)} hover:opacity-80`
                                                        }`}
                                                    >
                                                        {tag}{count > 0 && <span className="ml-1 opacity-60">({count})</span>}
                                                        {!DEFAULT_TAGS.includes(tag) && (
                                                            <span
                                                                onClick={(e) => { e.stopPropagation(); handleDeleteTag(tag); }}
                                                                className="ml-1 hidden cursor-pointer group-hover:inline"
                                                                title="Supprimer"
                                                            >×</span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <div className="mt-3 border-t border-leadrs-stroke pt-3">
                                            <button
                                                onClick={() => setShowTagManager(!showTagManager)}
                                                className="font-geist text-[11px] text-leadrs-text-soft hover:text-leadrs-brand"
                                            >
                                                {showTagManager ? '− Annuler' : '+ Créer un tag'}
                                            </button>
                                            {showTagManager && (
                                                <div className="mt-2 flex items-center gap-2">
                                                    <input
                                                        type="text"
                                                        value={newTagInput}
                                                        onChange={(e) => setNewTagInput(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                                                        placeholder="Ex: Rappel J+3"
                                                        className="min-w-0 flex-1 rounded-lg border border-leadrs-stroke px-2 py-1 font-geist text-xs focus:border-leadrs-brand focus:outline-none"
                                                    />
                                                    <button
                                                        onClick={handleAddTag}
                                                        disabled={!newTagInput.trim() || tags.includes(newTagInput.trim())}
                                                        className="rounded-lg bg-leadrs-brand px-3 py-1 font-geist text-xs font-medium text-white transition-opacity hover:opacity-80 disabled:opacity-40"
                                                    >
                                                        OK
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Comment dropdown */}
                            <div className="relative">
                                <button
                                    onClick={() => setOpenFilter(openFilter === 'comment' ? null : 'comment')}
                                    className={`inline-flex h-10 items-center gap-1.5 rounded-xl border px-3 font-geist text-sm transition-colors focus:outline-none ${
                                        commentFilter !== 'all'
                                            ? 'border-leadrs-brand bg-leadrs-brand text-white'
                                            : openFilter === 'comment'
                                            ? 'border-leadrs-brand text-leadrs-brand'
                                            : 'border-leadrs-stroke text-leadrs-text-soft hover:border-leadrs-brand hover:text-leadrs-brand'
                                    }`}
                                >
                                    <span>{commentFilter === 'all' ? 'Commentaire' : commentFilter === 'with' ? 'Avec' : 'Sans'}</span>
                                    <MdExpandMore className={`h-4 w-4 flex-shrink-0 transition-transform duration-150 ${openFilter === 'comment' ? 'rotate-180' : ''}`} />
                                </button>
                                {openFilter === 'comment' && (
                                    <div className="absolute left-0 top-[calc(100%+6px)] z-50 w-48 rounded-xl border border-leadrs-stroke bg-white p-1.5 shadow-md">
                                        {(['all', 'with', 'without'] as const).map((f) => (
                                            <button
                                                key={f}
                                                onClick={() => { setCommentFilter(f); setOpenFilter(null); }}
                                                className={`flex w-full items-center rounded-lg px-3 py-2 font-geist text-sm transition-colors ${
                                                    commentFilter === f
                                                        ? 'bg-leadrs-bg font-medium text-leadrs-brand'
                                                        : 'text-leadrs-text hover:bg-leadrs-bg'
                                                }`}
                                            >
                                                {f === 'all' ? 'Tous' : f === 'with' ? 'Avec commentaire' : 'Sans commentaire'}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Reset — visible only when filters are active */}
                            {(selectedTagFilter || commentFilter !== 'all' || search) && (
                                <button
                                    onClick={() => {
                                        setSelectedTagFilter('');
                                        setCommentFilter('all');
                                        setSearch('');
                                    }}
                                    className="inline-flex h-10 items-center rounded-xl border border-leadrs-stroke px-3 font-geist text-sm text-leadrs-text-soft transition-colors hover:border-leadrs-brand hover:text-leadrs-brand"
                                    title="Réinitialiser les filtres"
                                >
                                    ×
                                </button>
                            )}
                        </div>

                        {/* Results count + exports */}
                        <div className="flex items-center gap-2">
                            <span className="rounded-full bg-leadrs-bg px-3 py-1 font-geist text-xs font-medium text-leadrs-text-soft">
                                {filteredLeads.length} résultat{filteredLeads.length > 1 ? 's' : ''}
                                {!selectedTagFilter && total > leads.length ? ` / ${total}` : ''}
                            </span>
                            {selectedOffer === BOURSE_OFFER_KEY && (
                                <a
                                    href="/admin/dashboard?orderType=purchase-order"
                                    className="inline-flex items-center gap-2 rounded-xl bg-leadrs-brand px-3 py-2 font-geist text-sm font-semibold text-white transition hover:opacity-90"
                                >
                                    <MdStorefront className="h-4 w-4" />
                                    Acheter plus de leads
                                </a>
                            )}
                            {filteredLeads.length > 0 && (
                                <>
                                    <button
                                        onClick={downloadCSV}
                                        disabled={exporting}
                                        className="inline-flex items-center gap-2 rounded-xl border border-[#0F9D58] bg-[#0F9D58] px-3 py-2 font-geist text-sm font-medium text-white transition-opacity hover:opacity-80 disabled:opacity-50"
                                    >
                                        <MdFileDownload className="h-4 w-4" />
                                        <SiGooglesheets size={16} />
                                        <span>CSV</span>
                                    </button>
                                    <button
                                        onClick={downloadXLSX}
                                        disabled={exporting}
                                        className="inline-flex items-center gap-2 rounded-xl border border-[#217346] bg-[#217346] px-3 py-2 font-geist text-sm font-medium text-white transition-opacity hover:opacity-80 disabled:opacity-50"
                                    >
                                        <MdFileDownload className="h-4 w-4" />
                                        <SiGooglesheets size={16} />
                                        <span>XLSX</span>
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="rounded-xl border border-leadrs-stroke bg-white py-12">
                        <p className="text-center font-geist text-base text-leadrs-text-soft">Chargement…</p>
                    </div>
                ) : selectedOffer === BOURSE_OFFER_KEY && filteredLeads.length === 0 ? (
                    <div className="flex flex-col items-center gap-3 rounded-xl border border-leadrs-stroke bg-white py-14">
                        <MdLeaderboard className="h-8 w-8 text-leadrs-purple-focus" />
                        <p className="text-center font-geist text-base font-semibold text-leadrs-text">
                            Vous n'avez pas encore acheté de leads via la Bourse du Lead
                        </p>
                        <p className="max-w-md text-center font-geist text-sm text-leadrs-text-soft">
                            Découvrez les leads non attribués, de tout secteur, disponibles au prix minimum fixé par le vendeur.
                        </p>
                        <a
                            href="/admin/dashboard?orderType=purchase-order"
                            className="mt-1 rounded-xl bg-leadrs-brand px-4 py-2 font-geist text-sm font-semibold text-white transition hover:opacity-90"
                        >
                            Découvrir la Bourse du Lead
                        </a>
                    </div>
                ) : filteredLeads.length === 0 ? (
                    <div className="rounded-xl border border-leadrs-stroke bg-white py-12">
                        <p className="text-center font-geist text-base text-leadrs-text-soft">Aucun lead trouvé</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-2xl border border-leadrs-stroke bg-white">
                        <table className="w-full min-w-[1680px] text-left font-geist text-sm text-leadrs-text">
                            <thead className="sticky top-0 z-10 bg-leadrs-bg">
                                <tr>
                                    <th className="sticky left-0 z-50 bg-leadrs-bg px-3 py-3 font-semibold"># ID Mission</th>
                                    <th className="min-w-[160px] px-3 py-3 font-semibold">Tag</th>
                                    <th className="min-w-[140px] px-3 py-3 font-semibold">Statut</th>
                                    <th className="min-w-[140px] px-3 py-3 font-semibold">Ma note</th>
                                    <th className="min-w-[220px] px-3 py-3 font-semibold">Motif de signalement</th>
                                    <th className="min-w-[220px] px-3 py-3 font-semibold">Action</th>
                                    <th className="min-w-[260px] px-3 py-3 font-semibold">Commentaire</th>
                                    {bodyFields.map((f) => (
                                        <th key={f} className={`${colClassFor(f)} px-3 py-3 font-semibold`}>{f}</th>
                                    ))}
                                    <th className="min-w-[120px] px-3 py-3 font-semibold">Avancement</th>
                                    <th className="min-w-[120px] px-3 py-3 font-semibold">Note leadrs</th>
                                    <th className="min-w-[100px] px-3 py-3 font-semibold">DPE</th>
                                    <th className="min-w-[280px] px-3 py-3 font-semibold">Consentement RGPD</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredLeads.map((item, index) => (
                                    <tr
                                        key={item._id || index}
                                        className="border-t border-leadrs-stroke bg-white transition-colors hover:bg-leadrs-bg"
                                    >
                                        <td className="sticky left-0 z-[1] bg-leadrs-bg px-3 py-3 align-top shadow-[1px_0_0_#F3EDE4]">
                                            <div className="flex items-center gap-2">
                                                <p className="font-geist text-sm text-leadrs-text">
                                                    #{item.offerBuyerNumber?.toString().slice(0, 6) || ''}
                                                </p>
                                                {isLeadNew(item) && (
                                                    <span className="animate-pulse rounded-full bg-leadrs-yellow-focus px-2 py-0.5 font-geist text-[10px] font-bold uppercase text-white">
                                                        Nouveau
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-1 flex items-center gap-2">
                                                {item?.createdAt && isWithin30Minutes(item.createdAt) && (
                                                    <span className="relative flex h-2 w-2">
                                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-leadrs-brand opacity-50" />
                                                        <span className="relative inline-flex h-2 w-2 rounded-full bg-leadrs-brand opacity-50" />
                                                    </span>
                                                )}
                                                {item?.createdAt && (
                                                    <p className="font-geist text-xs text-leadrs-text-soft">{timeAgo(item.createdAt)}</p>
                                                )}
                                            </div>
                                        </td>

                                        <td className="px-3 py-3 align-top">
                                            <select
                                                value={leadTags[item._id] ?? ''}
                                                onChange={(e) => handleSetLeadTag(item._id, e.target.value)}
                                                className={`h-8 w-[150px] rounded-full border px-2 font-geist text-xs font-medium focus:outline-none ${
                                                    leadTags[item._id] ? getTagColor(leadTags[item._id]) : 'border-leadrs-stroke bg-white text-leadrs-text-soft'
                                                }`}
                                            >
                                                <option value="">— Aucun tag —</option>
                                                {tags.map((tag) => (
                                                    <option key={tag} value={tag}>{tag}</option>
                                                ))}
                                            </select>
                                        </td>

                                        <td className="px-3 py-3 align-top">
                                            {(() => {
                                                const s = getLeadStatus(item);
                                                const c = STATUS_COLOR_CLASSES[s.color];
                                                return (
                                                    <span
                                                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-geist text-xs font-semibold ${c.badge}`}
                                                        title={s.description}
                                                    >
                                                        {s.icon}
                                                        {s.label}
                                                    </span>
                                                );
                                            })()}
                                        </td>

                                        <td className="px-3 py-3 align-top">
                                            <div className="flex items-center space-x-1">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <button
                                                        key={star}
                                                        onClick={() => handleCustomerRateChange(item._id, star)}
                                                        className="text-xl focus:outline-none"
                                                        title={`${star} étoile${star > 1 ? 's' : ''}`}
                                                    >
                                                        <span className={star <= item.customerRate ? 'text-yellow-400' : 'text-leadrs-stroke'}>★</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </td>

                                        <td className="px-3 py-3 align-top">
                                            <select
                                                id={`reason-${item._id}`}
                                                value={item.reasons?.[0] ?? ''}
                                                onChange={async (e) => {
                                                    const v = e.target.value;
                                                    try {
                                                        await handleLeadChange(item._id, v || '', item.comment ?? '');
                                                        toast.success('Motif mis à jour ✅', { autoClose: 1800 });
                                                    } catch (err) {
                                                        toast.error('Échec de mise à jour du motif ❌');
                                                    }
                                                }}
                                                className="h-8 w-[180px] truncate rounded-xl border border-leadrs-stroke bg-white pl-3 pr-8 font-geist text-xs text-leadrs-text focus:border-leadrs-brand focus:outline-none"
                                            >
                                                <option value="">{item.status ? item.status : 'Sélectionner un motif'}</option>
                                                {REASON_OPTIONS.filter((opt) => opt !== item.status).map((opt) => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                            </select>
                                        </td>

                                        <td className="px-3 py-3 align-top">
                                            {item.approvalStatus === 'conflict' ? (
                                                <div className="inline-flex flex-col gap-1 rounded-xl border border-leadrs-red-focus bg-leadrs-red-soft px-4 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <svg className="h-4 w-4 text-leadrs-red-focus" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="font-geist text-xs font-semibold text-leadrs-red-focus">Conflit en cours</span>
                                                    </div>
                                                    <p className="font-geist text-[11px] leading-tight text-leadrs-red-focus opacity-70">Notre équipe va analyser ce lead.</p>
                                                </div>
                                            ) : item.approvalStatus === 'approved' ? (
                                                <div className="inline-flex flex-col gap-1 rounded-xl border border-leadrs-purple-focus bg-leadrs-purple-soft px-4 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <svg className="h-4 w-4 text-leadrs-purple-focus" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="font-geist text-xs font-semibold text-leadrs-purple-focus">Lead comptabilisé</span>
                                                    </div>
                                                    <p className="font-geist text-[11px] leading-tight text-leadrs-purple-focus opacity-70">Ce lead est conforme et sera facturé.</p>
                                                </div>
                                            ) : item.approvalStatus === 'rejected' ? (
                                                <div className="inline-flex flex-col gap-1 rounded-xl border border-leadrs-green-focus bg-leadrs-green-soft px-4 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <svg className="h-4 w-4 text-leadrs-green-focus" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="font-geist text-xs font-semibold text-leadrs-green-focus">Non facturé</span>
                                                    </div>
                                                    <p className="font-geist text-[11px] leading-tight text-leadrs-green-focus opacity-70">Contestation acceptée. Aucun paiement dû.</p>
                                                </div>
                                            ) : !item.status && !item.reasons?.[0] ? (
                                                <div className="inline-flex flex-col gap-1 rounded-xl border border-leadrs-stroke bg-leadrs-bg px-4 py-2.5">
                                                    <div className="flex items-center gap-2">
                                                        <svg className="h-4 w-4 text-leadrs-text-soft" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                        </svg>
                                                        <span className="font-geist text-xs font-semibold text-leadrs-text-soft">Action requise</span>
                                                    </div>
                                                    <p className="font-geist text-[11px] leading-tight text-leadrs-text-soft">Sélectionnez un <b>motif</b> avant de signaler.</p>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => handleLeadProblem(item._id)}
                                                    className="inline-flex items-center gap-2 rounded-xl border border-leadrs-red-focus bg-leadrs-red-soft px-4 py-2.5 transition-all hover:opacity-80"
                                                >
                                                    <svg className="h-4 w-4 text-leadrs-red-focus" fill="currentColor" viewBox="0 0 20 20">
                                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                    <span className="font-geist text-xs font-semibold text-leadrs-red-focus">Signaler un problème</span>
                                                </button>
                                            )}
                                        </td>

                                        <td className="px-3 py-3 align-top">
                                            <textarea
                                                defaultValue={item.comment ?? ''}
                                                placeholder="Ajouter un commentaire…"
                                                onChange={(e) => scheduleCommentSave(item, e.currentTarget.value)}
                                                className="h-20 w-[260px] resize-none rounded-xl border border-leadrs-stroke p-2 font-geist text-sm text-leadrs-text focus:border-leadrs-brand focus:outline-none"
                                            />
                                        </td>

                                        {bodyFields.map((f) => (
                                            <td key={f} className="max-w-[320px] px-3 py-3 align-top">
                                                <div className="break-words font-geist text-sm text-leadrs-text">{getBodyAnswer(item, f)}</div>
                                            </td>
                                        ))}

                                        <td className="px-3 py-3 align-top font-geist text-sm text-leadrs-text">
                                            {item.totalLeads && item.totalLeads > 1 ? `${item.index}/${item.totalLeads}` : 'N/A'}
                                        </td>

                                        <td className="px-3 py-3 align-top">
                                            {item.leadrsRate === 0 && (
                                                <span className="rounded-full bg-leadrs-red-soft px-2 py-1 font-geist text-xs text-leadrs-red-focus">{item.leadrsRate}/10</span>
                                            )}
                                            {item.leadrsRate >= 7 && (
                                                <span className="rounded-full bg-leadrs-green-soft px-2 py-1 font-geist text-xs text-leadrs-green-focus">{item.leadrsRate}/10</span>
                                            )}
                                            {item.leadrsRate < 7 && item.leadrsRate > 0 && (
                                                <span className="rounded-full bg-leadrs-yellow-soft px-2 py-1 font-geist text-xs text-leadrs-yellow-focus">{item.leadrsRate}/10</span>
                                            )}
                                        </td>

                                        <td className="px-3 py-3 align-top">
                                            {item.etiquetteDPE !== undefined ? (
                                                <div className="flex flex-col items-start gap-1">
                                                    <DpeBadge label={item.etiquetteDPE} />
                                                    {item.typeEnergieChauffage && (
                                                        <span className="text-[10px] leading-tight text-gray-500">{item.typeEnergieChauffage}</span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400">—</span>
                                            )}
                                        </td>

                                        <td className="px-3 py-3 align-top">
                                            <ConsentBadge consent={item.gdprConsent} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {!loading && page < totalPages && (
                    <div className="flex justify-center py-2">
                        <button
                            onClick={() => fetchLeadsPage(page + 1, { append: true })}
                            disabled={loadingMore}
                            className="inline-flex items-center gap-2 rounded-xl border border-leadrs-stroke bg-white px-4 py-2 font-geist text-sm font-medium text-leadrs-text transition-colors hover:border-leadrs-brand hover:text-leadrs-brand disabled:opacity-50"
                        >
                            {loadingMore ? 'Chargement…' : `Charger plus (${total - leads.length} restants)`}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MyLeadsBuyer;
